import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FormError } from "@/components/ui/form"
import { ScrollArea } from "@/components/ui/scroll-area"
import { LabelAndSelectValue, Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select"
import { useResetDependentAnswers } from "@/modules/patients/detail/better-doc/de/components/better-doc-consultation-questionnaire/useResetDependentAnswers"
import type { BetterDocConsultationQuestionId } from "@/modules/patients/detail/better-doc/de/lib/better-doc-consultation-questionnaire-definition"
import { getQuestionTestIdPrefix } from "@/modules/patients/detail/better-doc/de/lib/better-doc-consultation-questionnaire-test-ids"
import type { DeBetterDocSearchFormType } from "@/modules/patients/detail/better-doc/de/schema/de-better-doc-search-schema"

type BetterDocQuestionnaireSelectProps = {
  questionId: BetterDocConsultationQuestionId
  options: readonly string[]
}

export const BetterDocQuestionnaireSelect = ({ questionId, options }: BetterDocQuestionnaireSelectProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext<DeBetterDocSearchFormType>()

  const resetDependentAnswers = useResetDependentAnswers(questionId)

  const testIdPrefix = getQuestionTestIdPrefix(questionId)
  const translationPrefix = `episodeOfCare.betterDoc.searchDialog.consultationQuestionnaire.${questionId}`

  return (
    <div className="w-full">
      <Controller
        control={control}
        name={questionId}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const selectedOption = options.find((option) => option === value)

          return (
            <>
              <Select
                value={value ?? ""}
                onValueChange={(nextAnswer) => {
                  onChange(nextAnswer)
                  resetDependentAnswers()
                }}>
                <SelectTrigger data-testid={`${testIdPrefix}-select-trigger`} hasError={!!error}>
                  <LabelAndSelectValue
                    required
                    label={t(`${translationPrefix}.label`)}
                    value={selectedOption ? t(`${translationPrefix}.options.${selectedOption.toUpperCase()}`) : ""}
                  />
                </SelectTrigger>
                <SelectContent className="max-w-(--radix-select-trigger-width)">
                  <ScrollArea className="max-h-64">
                    <div className="py-2">
                      {options.map((option) => (
                        <SelectItem key={option} value={option} data-testid={`${testIdPrefix}-option-${option}`}>
                          {t(`${translationPrefix}.options.${option.toUpperCase()}`)}
                        </SelectItem>
                      ))}
                    </div>
                  </ScrollArea>
                </SelectContent>
              </Select>
              {error?.message && <FormError>{t(error.message)}</FormError>}
            </>
          )
        }}
      />
    </div>
  )
}
